import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useRegistry, ServiceDog } from '../context/RegistryContext';
import { CheckCircle2, Clipboard, AlertCircle } from 'lucide-react';

const ConfirmationPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getServiceDogById } = useRegistry();

  const [dog, setDog] = useState<ServiceDog | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('Invalid registry ID');
      setLoading(false);
      return;
    }

    const fetchDog = async () => {
      try {
        console.log("Fetching dog with ID:", id);
        const serviceDog = await getServiceDogById(id);

        if (!serviceDog) {
          console.log("No dog found with ID:", id);
          setError('Service dog not found');
        } else {
          console.log("Successfully retrieved dog:", serviceDog.registryNumber);
          setDog(serviceDog);
        }
      } catch (err) {
        console.error('Error fetching service dog:', err);

        // Create a placeholder dog object for temporary IDs
        if (id.startsWith('temp-')) {
          console.log("Creating placeholder for temporary ID:", id);
          const placeholderDog = {
            id: id,
            registryNumber: "TEMP-" + id.substring(5, 11),
            dogName: "Registration Pending",
            dogBreed: "Unknown",
            dogAge: "Unknown",
            dogPhoto: "",
            ownerName: "Registration Pending",
            ownerAddress: "Registration Pending",
            ownerPhone: "Registration Pending",
            ownerEmail: "Registration Pending",
            serviceType: "Service Dog",
            certificationFile: "",
            registrationDate: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
            status: 'pending' as 'pending'
          };
          setDog(placeholderDog);
        } else {
          setError('An error occurred while retrieving the registry information. Your registration has been received, but there was a problem displaying the details.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDog();
  }, [id, getServiceDogById]);

  const copyToClipboard = () => {
    if (dog) {
      navigator.clipboard.writeText(dog.registryNumber)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Error copying to clipboard:', err);
        });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-primary-200 h-12 w-12 mb-4"></div>
          <div className="h-4 bg-primary-200 rounded w-32 mb-3"></div>
          <div className="h-3 bg-primary-100 rounded w-24"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8 animate-fade-in">
        <div className="flex items-start text-accent-600 mb-6">
          <AlertCircle size={28} className="mr-3 mt-0.5" />
          <div>
            <h1 className="text-xl font-bold">Registration Status</h1>
            <p className="text-neutral-600 mt-1">{error}</p>
            <p className="text-neutral-600 mt-3">
              Your registration has likely been submitted successfully, but there was an issue displaying the confirmation details.
              You can try refreshing this page or checking your registration status later using the lookup feature.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg shadow transition-colors"
          >
            Refresh Page
          </button>
          <button
            onClick={() => navigate('/lookup')}
            className="bg-white border border-primary-600 text-primary-600 hover:bg-primary-50 px-6 py-2 rounded-lg shadow-sm transition-colors"
          >
            Verify Registration
          </button>
          <button
            onClick={() => navigate('/')}
            className="bg-neutral-100 hover:bg-neutral-200 text-neutral-700 px-6 py-2 rounded-lg shadow-sm transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!dog) {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-neutral-200">
        <div className="bg-success-600 text-white p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="text-2xl font-bold">Registration Successful!</h1>
          <p className="mt-2 text-success-100">
            Your service dog has been successfully registered in the US Service Dog Registry.
          </p>
        </div>

        <div className="p-6">
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-primary-700 mb-4">Registration Details</h2>
            <div className="bg-primary-50 border border-primary-100 rounded-lg p-5">
              <div className="mb-4">
                <p className="text-sm text-neutral-600 mb-1">Registry Number:</p>
                <div className="flex items-center">
                  <div className="bg-white border border-primary-300 rounded px-4 py-2 flex-grow">
                    <span className="font-mono font-bold text-lg text-primary-800">{dog.registryNumber}</span>
                  </div>
                  <button
                    onClick={copyToClipboard}
                    className="ml-2 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-lg transition-colors"
                    aria-label="Copy registry number"
                  >
                    <Clipboard size={20} />
                  </button>
                </div>
                {copied && (
                  <p className="text-xs text-success-600 mt-1">Registry number copied to clipboard!</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-600 mb-1">Dog Name:</p>
                  <p className="font-semibold text-neutral-800">{dog.dogName}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-600 mb-1">Registration Date:</p>
                  <p className="font-semibold text-neutral-800">
                    {new Date(dog.registrationDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-lg font-semibold text-primary-700 mb-4">Next Steps</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="bg-primary-100 text-primary-800 w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-medium text-neutral-800">Save Your Registry Number</h3>
                  <p className="text-neutral-600 mt-1">
                    Keep your registry number in a safe place. You will need it to verify your service dog's registration.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-primary-100 text-primary-800 w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-medium text-neutral-800">View & Print Your Registry Page</h3>
                  <p className="text-neutral-600 mt-1">
                    View and print your official service dog registry page for identification purposes while you await your ID.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-primary-100 text-primary-800 w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-medium text-neutral-800">Verify Your Registration</h3>
                  <p className="text-neutral-600 mt-1">
                    Use the verify page to check your service dog's registration status at any time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link
              to={`/registry/${dog.id}`}
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-lg shadow text-center transition-colors"
            >
              View Registry Card
            </Link>
            <Link
              to="/"
              className="bg-white border border-neutral-300 hover:bg-neutral-50 text-neutral-800 font-medium py-3 px-6 rounded-lg shadow-sm text-center transition-colors"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;